import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Observable } from 'rxjs';
import { ReplaySubject, BehaviorSubject } from 'rxjs';
import { taskconfig } from '../../notifications/shared/configurations';
import { NotificationType } from '../../notifications/shared/enum';
import { GroupedTaskNotifications, NotificationsCount, TaskNotifications } from '../../notifications/shared/interface';
import { LocalStorageKeys, StatusCode } from '../_enums/Common';
import { DesktopNotify, NotifiactionsDTO } from '../_models/jobsAndTalents/jobsAndTalentsModels';
import { CommonService } from './app.common.service';
import { RoleAccessService } from './role.access.service';
@Injectable({
  providedIn: 'root'
})
export class EnhancedTaskService {
  notifications: TaskNotifications[] = [];
  notificationsCount: NotificationsCount | null = null;
  private notificationChangedSubject = new Subject();
  notificationChanged$ = this.notificationChangedSubject.asObservable();
  constructor(public common: CommonService, public roleService: RoleAccessService) {
  }
  notifyOnNotificationChanged() {
    this.notificationChangedSubject.next(null);
  }

  getEnhancedNotifications(): Promise<number> {
    const cacheKey = LocalStorageKeys.EnhancedNotifications;
    const cachedCount = this.getCachedNotificationCount(cacheKey);
    if (cachedCount !== null) {
      return Promise.resolve(cachedCount);
    }
    return this.fetchAndProcessEnhancedNotifications(cacheKey);
  }
  getCachedNotificationCount(cacheKey: string): number | null {
    const notificationData = this.common.GetFromLocalStorage(cacheKey);
    if (notificationData) {
      this.notificationsCount = this.common.JsonParseData(notificationData);
      return this.notificationsCount?.count ?? 0;
    }
    return null;
  }
  fetchAndProcessEnhancedNotifications(cacheKey: string): Promise<number> {
    return Promise.all([
      this.getMyTasks(),
      this.getEmailAndTextNotifications()
    ]).then(([tasks, enhanced]) => {
      this.notifications = tasks;
      const taskCount = this.getTasksCount(this.notifications);
      const myTaskCount = this.getTasksCount(this.notifications);

      this.notificationsCount = this.buildNotificationCount(taskCount, myTaskCount, enhanced?.textCount ?? 0, enhanced?.emailCount ?? 0, enhanced.permission);
      this.common.SetLocalStorage(cacheKey, JSON.stringify(this.notificationsCount));

      return this.notificationsCount?.count ?? 0;
    });
  }

  getMyTasks(): Promise<TaskNotifications[]> {
    return this.common.GetData(this.common.ApiUrls.HomePage.GetMyTask + "TaskNotification", true).then(data => {
      if (data.status === StatusCode.OK) {
        this.notifications = data.resultData;
        return this.checkTasksSecurityRoleAccess(this.notifications);
      }
      return [];
    });
  }

  getEmailAndTextNotifications(): Promise<any> {
    return this.common.GetData(this.common.ApiUrls.HomePage.GetEnancedNotifications).then(data => {
      return data.status === StatusCode.OK ? data.resultData : null;
    });
  }

  buildNotificationCount(taskCount: number, myTaskCount: number, textCount: number, emailCount: number, permissions: DesktopNotify): NotificationsCount {
    if (permissions.isJobs.toLowerCase() != NotificationType.IsTabAccess) { emailCount = textCount = 0 }
    return { taskCount, myTaskCount, textCount, emailCount, count: taskCount + textCount + emailCount, permissions };
  }

  checkTasksSecurityRoleAccess(notifications: TaskNotifications[]): TaskNotifications[] {
    return notifications.filter(task => {
      const hasRecords = (task.matchedRecords?.length ?? 0) > 0;
      const accessRule = taskconfig.find(rule => rule.ruleName === task.ruleName);
      return hasRecords && accessRule && this.roleService.HasAccess(accessRule.securityRoleKey);
    });
  }

  enrichTaskDisplayProperties(notifications: TaskNotifications[]): void {
    notifications.forEach(notification => {
      const ruleConfig = taskconfig.find(task => task.ruleName === notification.ruleName);
      if (ruleConfig) {
        notification.categoryName = this.common.GetTranslation("Notification.Tasks." + ruleConfig.categoryTranslationKey);
        notification.taskHeading = this.common.GetTranslation("Notification.Tasks." + ruleConfig.taskHeading);
        notification.taskInstructions = this.common.GetTranslation("Notification.Tasks." + ruleConfig.taskInstructions);
        notification.icon = ruleConfig.icon;
        notification.buttonTextForParentCategoryNavigation = this.common.GetTranslation("Notification.Tasks." + ruleConfig.buttonTextForParentCategoryNavigation);
        notification.iconAltText = this.common.GetTranslation("Notification.Tasks." + ruleConfig.iconAltTextKey);

        if (notification.matchedRecords?.length) {
          notification.matchedRecords.forEach(record => {
            record.viewIndivsualTaskDetail = this.common.GetTranslation("Notification.Tasks." + ruleConfig.viewTaskDetailTranslationKey)
              .replace(/<<candidateName>>/g, record.candidateName)
              .replace(/<<jobRefrence>>/g, record.jobReference)
              .replace(/<<jobTitle>>/g, record.jobTitle)
              .replace(/<<noOfApplications>>/g, record.numberOfCandidates);
          });
        }
      }
    });
  }

  getTasksCount(notifications: TaskNotifications[]): number {
    let count = 0;
    notifications.forEach(task => {
      task.count = task.matchedRecordsCount = task.matchedRecords?.length ?? 0;
      let candidateCount = task.matchedRecords?.reduce((sum, record) => sum + (record?.numberOfCandidates ?? 0), 0) ?? 0;
      candidateCount = candidateCount > 0 ? candidateCount : task.count;

      if (task.taskInstructions) {
        task.taskInstructions = task.taskInstructions
          .replace(/{jobCount}/g, task.count.toString())
          .replace(/{candidateCount}/g, candidateCount.toString())
      }
      count += task.count;
    });
    return count;
  }

  groupTaskByCategory(notifications: TaskNotifications[]): GroupedTaskNotifications[] {
    const grouping = new Map<string, TaskNotifications[]>();
    for (const task of notifications) {
      const key = task.categoryName;
      if (!grouping.has(key)) {
        grouping.set(key, []);
      }
      grouping.get(key)!.push(task);
    }

    return Array.from(grouping.entries())
      .map(([categoryName, groupedNotifications]) => {
        const categoryOrder = taskconfig.find(cfg => this.common.GetTranslation("Notification.Tasks." + cfg.categoryTranslationKey) === categoryName)?.categoryDisplayOrder ?? 999;

        const sortedTasks = groupedNotifications.sort((a, b) => {
          const orderA = taskconfig.find(cfg => cfg.ruleName === a.ruleName)?.displayOrder ?? 999;
          const orderB = taskconfig.find(cfg => cfg.ruleName === b.ruleName)?.displayOrder ?? 999;
          return orderA - orderB;
        });
        return {
          categoryName,
          groupedNotifications: sortedTasks,
          buttonTextForParentCategoryNavigation: sortedTasks[0]?.buttonTextForParentCategoryNavigation,
          ariaLabelTextForParentCategoryButton: sortedTasks[0]?.ariaLabelTextForParentCategoryButton,
          displayOrder: categoryOrder
        };
      })
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }
 
}
